import React from 'react';
import { Dialog } from '@headlessui/react';
import { X, AlertTriangle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { syncInitialData } from '../services/sync';
import { supabase } from '../lib/supabase';

const credentialsSchema = z.object({
  clientId: z.string().min(1, 'Client ID is required'),
  clientSecret: z.string().min(1, 'Client Secret is required'),
  facilityId: z.number().optional(),
  facilityName: z.string().optional(),
  advanced: z.boolean().optional(),
  endpointUrl: z.string().optional(),
  subscriptionKey: z.string().optional(),
});

type CredentialsFormData = z.infer<typeof credentialsSchema>;

function formatFacilityLabel(facility: {
  FacilityLocationName: string;
  FacilityName: string | null;
}) {
  return facility.FacilityName
    ? `${facility.FacilityLocationName} (${facility.FacilityName})`
    : facility.FacilityLocationName;
}

type FourDCredentialsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CredentialsFormData & { facilityId?: number, facilityName?: string }) => void;
  isLoading?: boolean;
  initialData?: {
    clientId: string;
    clientSecret: string;
    subscriptionKey: string;
    facilityId?: number;
    facilityName?: string;
    endpointUrl?: string;
  };
  facilities?: Array<{
    FacilityLocationId: number;
    FacilityLocationName: string;
    FacilityName: string | null;
  }>;
};

export default function FourDCredentialsModal({
  isOpen,
  onClose,
  onSave,
  isLoading = false,
  initialData,
  facilities,
}: FourDCredentialsModalProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<CredentialsFormData>({
    resolver: zodResolver(credentialsSchema),
    defaultValues: {
      ...initialData,
      endpointUrl: initialData?.endpointUrl || 'https://api.4d-emr.com/api/public',
      subscriptionKey: initialData?.subscriptionKey || 'd30017297ea649f08aaa1a33ef36986d',
      advanced: false,
    },
  });

  const [selectedFacility, setSelectedFacility] = React.useState<{
    id: number;
    name: string;
  } | null>(initialData?.facilityId && initialData?.facilityName ? {
    id: initialData.facilityId,
    name: initialData.facilityName
  } : null);
  
  const [showFacilityWarning, setShowFacilityWarning] = React.useState(false);
  const [isChangingFacility, setIsChangingFacility] = React.useState(false);
  const [hasTestedConnection, setHasTestedConnection] = React.useState(false);
  const [showAdvanced, setShowAdvanced] = React.useState(false);
  const [syncInProgress, setSyncInProgress] = React.useState(false);
  const [syncStep, setSyncStep] = React.useState('');
  const [progress, setProgress] = React.useState(0);
  const [syncSummary, setSyncSummary] = React.useState<any>(null);

  React.useEffect(() => {
    if (initialData) {
      reset(initialData);
      if (initialData.facilityId && initialData.facilityName) {
        setSelectedFacility({
          id: initialData.facilityId,
          name: initialData.facilityName
        });
      }
    }
  }, [initialData, reset]);

  // After a successful test, populate facility picker (including single-facility accounts)
  React.useEffect(() => {
    if (!facilities?.length) return;

    const match = initialData?.facilityId
      ? facilities.find((f) => f.FacilityLocationId === initialData.facilityId)
      : null;
    const defaultFacility = match ?? facilities[0];

    setSelectedFacility({
      id: defaultFacility.FacilityLocationId,
      name: formatFacilityLabel(defaultFacility),
    });
  }, [facilities, initialData?.facilityId]);

  // Reset states when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setHasTestedConnection(false);
      setSelectedFacility(
        initialData?.facilityId && initialData?.facilityName
          ? { id: initialData.facilityId, name: initialData.facilityName }
          : null
      );
      setShowFacilityWarning(false);
      setIsChangingFacility(false);
    }
  }, [isOpen, initialData?.facilityId, initialData?.facilityName]);

  const handleFacilityChange = (facilityId: number) => {
    const facility = facilities?.find(f => f.FacilityLocationId === facilityId);
    if (facility) {
      if (initialData?.facilityId && initialData.facilityId !== facilityId) {
        setShowFacilityWarning(true);
        setIsChangingFacility(true);
      } else {
        setSelectedFacility({
          id: facility.FacilityLocationId,
          name: formatFacilityLabel(facility)
        });
      }
    }
  };

  const confirmFacilityChange = () => {
    const facilityId = parseInt(document.querySelector<HTMLSelectElement>('select[name="facilityId"]')?.value || '0');
    const facility = facilities?.find(f => f.FacilityLocationId === facilityId);
    if (facility) {
      setSelectedFacility({
        id: facility.FacilityLocationId,
        name: formatFacilityLabel(facility)
      });
    }
    setShowFacilityWarning(false);
    setIsChangingFacility(false);
  };

  const onSubmit = async (data: CredentialsFormData) => {
    try {
      if (!hasTestedConnection) {
        // First test the connection
        await onSave({
          clientId: data.clientId,
          clientSecret: data.clientSecret,
          subscriptionKey: data.subscriptionKey
        });
        setHasTestedConnection(true);
        return;
      }

      if (selectedFacility) {
        setSyncInProgress(true);
        setSyncStep('Preparing to sync...');
        setProgress(0);
        setSyncSummary(null);
        await onSave({
          ...data,
          facilityId: selectedFacility.id,
          facilityName: selectedFacility.name,
        });
        // Start sync with progress callbacks
        const session = (await supabase.auth.getSession()).data.session;
        if (!session || !session.user) throw new Error('User not authenticated');
        await syncInitialData(
          session.user.id,
          (step, prog) => {
            setSyncStep(step);
            setProgress(Math.max(0, Math.min(100, Math.floor(prog))));
          },
          (summary) => {
            setSyncSummary(summary);
            setSyncInProgress(false);
          },
          { fullHistoricalSync: true }
        );
        onClose();
      }
    } catch (error) {
      toast.error('Failed to save credentials');
      setSyncInProgress(false);
    }
  };

  // Progress Modal
  const ProgressModal = () => (
    <Dialog open={syncInProgress} onClose={() => {}} className="relative z-50">
      <div className="fixed inset-0 bg-black/60" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel tabIndex={-1} className="mx-auto max-w-md w-full bg-white rounded-xl shadow-lg p-8 flex flex-col items-center">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Synchronizing Data...</h3>
          <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
            <div
              className="bg-indigo-600 h-4 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-gray-700 text-center mb-2">{syncStep}</p>
          <p className="text-xs text-gray-400">Please keep this page open until the sync completes.</p>
        </Dialog.Panel>
      </div>
    </Dialog>
  );

  // Summary Modal
  const SummaryModal = () => (
    <Dialog open={!!syncSummary} onClose={() => setSyncSummary(null)} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-md w-full bg-white rounded-xl shadow-lg p-8 flex flex-col items-center">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Sync Complete</h3>
          {syncSummary?.success ? (
            <p className="text-green-700 mb-2">All data synchronized successfully!</p>
          ) : (
            <p className="text-red-700 mb-2">There were errors during sync.</p>
          )}
          {syncSummary?.error && (
            <pre className="text-xs text-red-500 bg-red-50 rounded p-2 w-full overflow-x-auto mb-2">{String(syncSummary.error)}</pre>
          )}
          <button
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
            onClick={() => setSyncSummary(null)}
          >
            Close
          </button>
        </Dialog.Panel>
      </div>
    </Dialog>
  );

  return (
    <>
      {syncInProgress && <ProgressModal />}
      {!!syncSummary && <SummaryModal />}
      <Dialog open={isOpen} onClose={onClose} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-md w-full bg-white rounded-xl shadow-lg">
            <div className="relative p-6">
              <button
                onClick={onClose}
                className="absolute right-6 top-6 text-gray-400 hover:text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>

              <h3 className="text-lg font-medium leading-6 text-gray-900 mb-8">
                Enter Your 4D EMR Credentials
              </h3>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Client ID
                  </label>
                  <input
                    type="text"
                    {...register('clientId')}
                    className="mt-2 block w-full px-3 py-2 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder="Enter your Client ID"
                  />
                  {errors.clientId && (
                    <p className="mt-1 text-sm text-red-600">{errors.clientId.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Client Secret
                  </label>
                  <input
                    type="password"
                    {...register('clientSecret')}
                    className="mt-2 block w-full px-3 py-2 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder="Enter your Client Secret"
                  />
                  {errors.clientSecret && (
                    <p className="mt-1 text-sm text-red-600">{errors.clientSecret.message}</p>
                  )}
                </div>

                {/* Advanced Toggle */}
                <div className="flex items-center mt-4">
                  <input
                    id="show-advanced"
                    type="checkbox"
                    checked={showAdvanced}
                    onChange={() => setShowAdvanced((v) => !v)}
                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                  />
                  <label htmlFor="show-advanced" className="ml-2 block text-sm text-gray-700 font-medium">
                    Advanced (temporary): Custom 4D Endpoint & Subscription Key
                  </label>
                </div>

                {showAdvanced && (
                  <div className="p-4 mt-2 border border-indigo-200 rounded bg-indigo-50">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        4D Endpoint URL base
                      </label>
                      <input
                        type="text"
                        {...register('endpointUrl')}
                        className="mt-2 block w-full px-3 py-2 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        placeholder="https://api.4d-emr.com/api/public"
                        defaultValue="https://api.4d-emr.com/api/public"
                      />
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700">
                        Subscription Key
                      </label>
                      <input
                        type="text"
                        {...register('subscriptionKey')}
                        className="mt-2 block w-full px-3 py-2 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        placeholder="d30017297ea649f08aaa1a33ef36986d"
                        defaultValue="d30017297ea649f08aaa1a33ef36986d"
                      />
                    </div>
                  </div>
                )}

                {initialData?.facilityName && !hasTestedConnection && !facilities?.length && (
                  <div className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700">
                    <span className="font-medium">Saved facility:</span>{' '}
                    {initialData.facilityName}
                    <p className="mt-1 text-xs text-gray-500">
                      Click Re-test Connection below to load facilities and change selection.
                    </p>
                  </div>
                )}

                {facilities && facilities.length >= 1 && (
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">
                      Select Facility
                    </label>
                    <select
                      disabled={!hasTestedConnection}
                      name="facilityId"
                      className="mt-2 block w-full px-3 py-2 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      value={selectedFacility?.id || ''}
                      onChange={(e) => handleFacilityChange(Number(e.target.value))}
                    >
                      <option value="">Select a facility</option>
                      {facilities.map((facility) => (
                        <option 
                          key={facility.FacilityLocationId} 
                          value={facility.FacilityLocationId}
                        >
                          {facility.FacilityLocationName}
                          {facility.FacilityName ? ` (${facility.FacilityName})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {showFacilityWarning && (
                  <div className="mt-4 p-4 bg-yellow-50 rounded-md">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <AlertTriangle className="h-5 w-5 text-yellow-400" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800">
                          Warning: Changing Facility
                        </h3>
                        <div className="mt-2 text-sm text-yellow-700">
                          <p>
                            Changing the facility will delete all existing synchronized data for the current facility.
                            This action cannot be undone. Are you sure you want to continue?
                          </p>
                        </div>
                        <div className="mt-4">
                          <div className="flex space-x-3">
                            <button
                              type="button"
                              onClick={confirmFacilityChange}
                              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-yellow-700 bg-yellow-100 hover:bg-yellow-200"
                            >
                              Yes, change facility
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setShowFacilityWarning(false);
                                setIsChangingFacility(false);
                                if (initialData?.facilityId) {
                                  setSelectedFacility({
                                    id: initialData.facilityId,
                                    name: initialData.facilityName || ''
                                  });
                                }
                              }}
                              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-8 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading || (hasTestedConnection && facilities && facilities.length >= 1 && !selectedFacility)}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading
                      ? 'Saving...'
                      : hasTestedConnection
                        ? 'Save Connection'
                        : initialData?.facilityId
                          ? 'Re-test Connection'
                          : 'Test Connection'}
                  </button>
                </div>
              </form>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </>
  );
}